<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-y-auto">
        <form @submit.prevent="saveSite">
            <div class="px-8 sm:px-8 lg:px-8 pt-6 pb-2">
                <div class="w-full xl:w-4/5 mx-auto flex items-center justify-between gap-4">
                    <div class="min-w-0 flex-1">
                        <h1 class="stage-page-title">{{ $t('Add website') }}</h1>
                    </div>
                    <div class="flex items-center">
                        <button class="btn p-4 rounded-none" type="button" @click="goBack">
                            <svg-vue class="h-5 w-5 text-gray-700" icon="font-awesome.chevron-left-regular"></svg-vue>
                        </button>
                    </div>
                </div>
            </div>

            <div class="stage-page-body">
                <div class="w-full xl:w-4/5 mx-auto bg-white shadow sm:rounded border border-bordergray-100 overflow-hidden">
                    <div class="p-6 md:grid md:grid-cols-3 md:gap-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Website') }}</h3>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-1 gap-5">
                                <div>
                                    <label class="stage-filter-label block mb-2">{{ $t('Title') }}</label>
                                    <input
                                        v-model.trim="form.title"
                                        class="stage-filter-input form-input block w-full text-sm"
                                        maxlength="255"
                                        required
                                        type="text"
                                    >
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2">URL</label>
                                    <input
                                        v-model.trim="form.url"
                                        class="stage-filter-input form-input block w-full text-sm"
                                        maxlength="255"
                                        required
                                        type="url"
                                    >
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2">{{ $t('Environment') }}</label>
                                    <select v-model="form.environment" class="stage-filter-input form-select block w-full text-sm">
                                        <option value="production">{{ $t('Production') }}</option>
                                        <option value="staging">Staging</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="border-t border-bordergray-100 p-6 md:grid md:grid-cols-3 md:gap-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Customers') }}</h3>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="border border-bordergray-100 rounded overflow-y-auto max-h-80 bg-white divide-y divide-bordergray-100">
                                <label
                                    v-for="customer in customerOptions"
                                    :key="'server-monitoring-new-customer-' + customer.id"
                                    class="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                >
                                    <input
                                        v-model="form.customer_ids"
                                        :value="customer.id"
                                        class="form-checkbox h-4 w-4 text-gray-600 transition duration-150 ease-in-out mr-3"
                                        type="checkbox"
                                    >
                                    <img
                                        :alt="customer.name"
                                        :src="resolveCustomerAvatar(customer)"
                                        class="h-6 w-6 rounded-full object-cover mr-2"
                                    >
                                    <span class="truncate">{{ customer.name }}</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div class="stage-footer-surface text-right px-6 py-3 border-t border-bordergray-100">
                        <button class="btn-stage-primary" type="submit">{{ $t('Save') }}</button>
                    </div>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
export default {
    name: "server-monitoring-new",
    metaInfo() {
        return {
            title: this.$t('Add website').toString()
        }
    },
    data() {
        return {
            loading: false,
            form: {
                title: '',
                url: '',
                environment: 'production',
                customer_ids: [],
            },
            customerOptions: [],
        };
    },
    mounted() {
        this.loadOptions();
    },
    methods: {
        goBack() {
            this.$router.push('/workspace/insights/server-monitoring');
        },
        loadOptions() {
            const self = this;
            self.loading = true;
            axios.get('api/workspace/insights/server-monitoring/options').then(function (response) {
                self.customerOptions = Array.isArray(response.data && response.data.customers)
                    ? response.data.customers
                    : [];
                self.loading = false;
            }).catch(function () {
                self.customerOptions = [];
                self.loading = false;
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$t('Customer list could not be loaded').toString(),
                    type: 'error'
                });
            });
        },
        resolveCustomerAvatar(customer) {
            if (customer && customer.avatar) {
                return customer.avatar;
            }
            return '/images/default/icon.png';
        },
        saveSite() {
            const self = this;
            if (!this.form.title || !this.form.url || !this.form.environment) {
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$t('Please fill in title, URL, and environment').toString(),
                    type: 'warning'
                });
                return;
            }

            if (!Array.isArray(this.form.customer_ids) || this.form.customer_ids.length < 1) {
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$t('Please select at least one customer').toString(),
                    type: 'warning'
                });
                return;
            }

            self.loading = true;
            axios.post('api/workspace/insights/server-monitoring', {
                title: self.form.title,
                url: self.form.url,
                environment: self.form.environment,
                customer_ids: self.form.customer_ids,
            }).then(function (response) {
                self.loading = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$t('Website was created').toString(),
                    type: 'success'
                });
                const created = response.data && response.data.item ? response.data.item : null;
                if (created && created.id) {
                    self.$router.push('/workspace/insights/server-monitoring/' + created.id + '/manage');
                    return;
                }
                self.$router.push('/workspace/insights/server-monitoring');
            }).catch(function () {
                self.loading = false;
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$t('Website could not be saved').toString(),
                    type: 'error'
                });
            });
        }
    }
}
</script>
